import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { Exercise } from '../types';
import { API_URL } from '../utils';

interface ExerciseContextType {
  exercises: Exercise[];
  exercisesLoading: boolean;
  exercisesError: string | null;
  reloadExercises: () => Promise<void>;
  setExercises: React.Dispatch<React.SetStateAction<Exercise[]>>;
}

const ExerciseContext = createContext<ExerciseContextType | undefined>(undefined);

export const ExerciseProvider: React.FC<{
  children: React.ReactNode;
  onServiceUnavailable: () => void;
}> = ({ children, onServiceUnavailable }) => {
  const { fetchWithAuth, idToken } = useAuth();

  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [exercisesLoading, setExercisesLoading] = useState(false);
  const [exercisesError, setExercisesError] = useState<string | null>(null);

  const loadExercises = useCallback(async () => {
    if (!idToken) return;

    setExercisesLoading(true);
    setExercisesError(null);

    try {
      const response = await fetchWithAuth(`${API_URL}/exercises`);
      if (!response.ok) {
        let errorMsg = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.message || errorMsg;
        } catch (e) {
          // Ignore if response body is not JSON or empty
        }
        throw new Error(errorMsg);
      }
      const data = await response.json();
      setExercises(data);
      setExercisesError(null);
    } catch (error: any) {
      console.error("Failed to fetch exercises:", error);
      setExercises([]); // Clear exercises on error

      // Only set service unavailable for actual service unavailable errors
      if (error.isServiceUnavailable) {
        onServiceUnavailable();
      } else {
        setExercisesError(error.message || 'Failed to load exercises');
      }
    } finally {
      setExercisesLoading(false);
    }
  }, [idToken, fetchWithAuth, onServiceUnavailable]);

  // Load exercises on mount and when idToken changes
  useEffect(() => {
    loadExercises();
  }, [loadExercises]);

  const reloadExercises = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/exercises`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setExercises(data);
      setExercisesError(null);
    } catch (error: any) {
      console.error("Failed to reload exercises:", error);
      if (error.isServiceUnavailable) {
        onServiceUnavailable();
      } else {
        setExercisesError(error.message || 'Failed to reload exercises');
      }
    }
  }, [fetchWithAuth, onServiceUnavailable]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    exercises,
    exercisesLoading,
    exercisesError,
    reloadExercises,
    setExercises,
  }), [exercises, exercisesLoading, exercisesError, reloadExercises]);

  return (
    <ExerciseContext.Provider value={value}>
      {children}
    </ExerciseContext.Provider>
  );
};

export const useExercise = () => {
  const context = useContext(ExerciseContext);
  if (context === undefined) {
    throw new Error('useExercise must be used within ExerciseProvider');
  }
  return context;
};
